/**
 * A headline number: label, value, optional note, optional sparkline.
 *
 * The form exists so a single current value is not drawn as a one-bar bar chart. The
 * value keeps the font's proportional figures - `tabular-nums` gives every digit the
 * width of a zero, which makes a number like 121 look loose at display sizes - while
 * the tiny trend beneath it is de-emphasised, because it is context rather than the
 * point.
 */

import type { ReactNode } from 'react'

import { cn } from '../lib/cn'
import { Sparkline } from './charts/Sparkline'
import type { SparkPoint } from './charts/Sparkline'

interface StatTileProps {
  label: string
  value: ReactNode
  /** A qualifier under the value - the window it covers, or why it is unknown. */
  note?: ReactNode
  /** Emphasis colour for the value. Defaults to primary ink. */
  valueClassName?: string
  trend?: SparkPoint[]
  trendLabel?: string
  icon?: ReactNode
  className?: string
}

export function StatTile({
  label,
  value,
  note,
  valueClassName,
  trend,
  trendLabel = 'value',
  icon,
  className,
}: StatTileProps) {
  return (
    <div className={cn('card flex flex-col gap-1 p-4', className)}>
      <div className="flex items-center gap-1.5 text-xs text-ink-secondary">
        {icon ? (
          <span aria-hidden className="text-ink-muted">
            {icon}
          </span>
        ) : null}
        <span>{label}</span>
      </div>

      <p className={cn('text-3xl leading-tight font-semibold text-ink', valueClassName)}>
        {value}
      </p>

      {note ? <p className="text-xs text-ink-muted">{note}</p> : null}

      {trend && trend.length > 1 ? (
        <Sparkline
          points={trend}
          height={32}
          // De-emphasised: the trend is context beside the value, not a second headline.
          stroke="var(--color-axis)"
          fill={false}
          valueLabel={trendLabel}
          className="mt-1"
        />
      ) : null}
    </div>
  )
}
